/**
 * JoinCallScreen — Scan QR or paste link to join a call.
 *
 * Flow: camera permission → scan QR → validate → connect → advance.
 * Fallback: manual paste input.
 */

import { useState, useEffect, useRef, useCallback, type ReactNode } from 'react';
import { createQrScanner, isQrScannerSupported, type QrScannerHandle } from './qrService.js';
import { decodePairingPayload, validatePairingPayload, parseJoinUrl, PairingError } from './pairingPayload.js';

interface Props {
  expectedChain: string;
  onJoin: (roomId: string, secret: string, signalingUrl: string, callerAddress: string) => void;
  onCancel: () => void;
}

type JoinState = 'initializing' | 'scanning' | 'manual' | 'connecting' | 'error';

export function JoinCallScreen({ expectedChain, onJoin, onCancel }: Props): ReactNode {
  const [state, setState] = useState<JoinState>('initializing');
  const [error, setError] = useState('');
  const [manualInput, setManualInput] = useState('');
  const [cameraSupported, setCameraSupported] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const scannerRef = useRef<QrScannerHandle | null>(null);
  const mountedRef = useRef(true);

  // Initialize: check deep-link, then check camera support, then start scanner
  useEffect(() => {
    mountedRef.current = true;

    // Check URL fragment for join payload (deep-link)
    const hash = window.location.hash.slice(1);
    if (hash) {
      handlePayloadString(hash);
      return;
    }

    // Check camera support then start
    void isQrScannerSupported().then((supported) => {
      if (!mountedRef.current) return;
      setCameraSupported(supported);
      if (supported) {
        startScanner();
      } else {
        setState('manual');
      }
    });

    return () => {
      mountedRef.current = false;
      scannerRef.current?.destroy();
      scannerRef.current = null;
    };
  }, []);

  const startScanner = useCallback(() => {
    setState('scanning');

    // Wait for next frame to ensure video element is in DOM
    setTimeout(() => {
      if (!mountedRef.current || !videoRef.current) {
        setState('manual');
        return;
      }

      scannerRef.current = createQrScanner(
        videoRef.current,
        (data) => {
          if (!mountedRef.current) return;
          scannerRef.current?.stop();
          handleScannedData(data);
        },
        (err) => {
          if (!mountedRef.current) return;
          setError(`Camera error: ${err.message}`);
          setState('manual');
        }
      );

      void scannerRef.current.start().catch((err) => {
        if (!mountedRef.current) return;
        setState('manual');
        setError('Camera access denied. Use the manual input below.');
      });
    }, 100);
  }, []);

  const handleScannedData = (data: string) => {
    // Try parsing as a full URL first
    const fromUrl = parseJoinUrl(data);
    if (fromUrl) {
      handlePayload(fromUrl);
      return;
    }
    // Try as raw base64 payload
    handlePayloadString(data);
  };

  const handlePayloadString = (encoded: string) => {
    try {
      const payload = decodePairingPayload(encoded);
      handlePayload(payload);
    } catch (err) {
      if (err instanceof PairingError) {
        setError(err.message);
      } else {
        setError('Invalid QR code or link');
      }
      if (state !== 'scanning') setState('manual');
    }
  };

  const handlePayload = (payload: ReturnType<typeof decodePairingPayload>) => {
    try {
      validatePairingPayload(payload, expectedChain);
      setState('connecting');
      onJoin(payload.roomId, payload.secret, payload.signalingUrl, payload.callerAddress);
    } catch (err) {
      if (err instanceof PairingError) {
        setError(err.message);
      } else {
        setError('Failed to validate pairing data');
      }
      setState(scannerRef.current ? 'scanning' : 'manual');
    }
  };

  const handleManualSubmit = () => {
    setError('');
    const input = manualInput.trim();
    if (!input) {
      setError('Paste a join link or QR payload');
      return;
    }
    const fromUrl = parseJoinUrl(input);
    if (fromUrl) {
      handlePayload(fromUrl);
      return;
    }
    const hashPart = input.includes('#') ? input.split('#')[1]! : input;
    handlePayloadString(hashPart);
  };

  const handleRetryCamera = useCallback(() => {
    setError('');
    scannerRef.current?.destroy();
    scannerRef.current = null;
    startScanner();
  }, [startScanner]);

  // ─── Render ───────────────────────────────────────────────────────────

  if (state === 'connecting') {
    return (
      <div className="screen">
        <div className="screen-body centered">
          <div className="loading-spinner" />
          <p>Connecting to peer...</p>
        </div>
      </div>
    );
  }

  if (state === 'initializing') {
    return (
      <div className="screen">
        <div className="screen-body centered">
          <div className="loading-spinner" />
          <p className="text-secondary">Initializing camera...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="screen">
      <div className="screen-header">
        <button className="btn-ghost" onClick={onCancel}>Cancel</button>
        <h2>Join Stream</h2>
      </div>
      <div className="screen-body">
        {state === 'scanning' && (
          <>
            <p className="text-secondary text-center">
              Point your camera at the QR code shown by the caller.
            </p>
            <div className="scanner-container">
              <video
                ref={videoRef}
                className="scanner-video"
                playsInline
                muted
                autoPlay
              />
            </div>
            <button className="btn-ghost btn-full" onClick={() => { scannerRef.current?.stop(); setState('manual'); }}>
              Or paste a link manually
            </button>
          </>
        )}

        {state === 'manual' && (
          <>
            <p className="text-secondary">
              Paste the join link shared by the caller.
            </p>

            <div className="form-group">
              <label>Join Link or Code</label>
              <textarea
                value={manualInput}
                onChange={e => setManualInput(e.target.value)}
                placeholder="Paste the join link here..."
                rows={3}
                autoFocus
                spellCheck={false}
              />
            </div>

            <button className="btn-primary btn-full" onClick={handleManualSubmit}>
              Join
            </button>

            {cameraSupported && (
              <button className="btn-ghost btn-full" onClick={handleRetryCamera}>
                Try camera again
              </button>
            )}
          </>
        )}

        {error && (
          <div className="error-message">
            {error}
            <button className="btn-ghost btn-xs" onClick={() => setError('')}>Dismiss</button>
          </div>
        )}
      </div>
    </div>
  );
}
